import { API_BASE_URL } from './api/config';

// Auth API
export const authAPI = {
  // Login admin
  login: async (credentials) => {
    const response = await fetch(`${API_BASE_URL}/admin/login`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(credentials),
    });
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'Login failed');
    }
    return response.json();
  },

  // Register admin (for initial setup)
  register: async (adminData) => {
    const response = await fetch(`${API_BASE_URL}/admin/register`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(adminData),
    });
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'Registration failed');
    }
    return response.json();
  },

  // Get current admin profile
  getProfile: async () => {
    const token = localStorage.getItem('adminToken');
    const response = await fetch(`${API_BASE_URL}/admin/profile`, {
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
    });
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'Failed to get profile');
    }
    return response.json();
  },

  // Change password
  changePassword: async (passwordData) => {
    const token = localStorage.getItem('adminToken');
    const response = await fetch(`${API_BASE_URL}/admin/change-password`, {
      method: 'PUT',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(passwordData),
    });
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'Failed to change password');
    }
    return response.json();
  },

  // Logout (client-side)
  logout: () => {
    localStorage.removeItem('adminToken');
    localStorage.removeItem('adminUser');
  }
};

// Helper function to get auth headers
export const getAuthHeaders = () => {
  const token = localStorage.getItem('adminToken');
  return {
    'Authorization': `Bearer ${token}`,
    'Content-Type': 'application/json',
  };
};

// Party Info API
export const getPartyInfo = async () => {
  const response = await fetch(`${API_BASE_URL}/party-info`);
  if (!response.ok) {
    throw new Error('Failed to fetch party info');
  }
  return response.json();
};

// Programs API
export const getPrograms = async () => {
  const response = await fetch(`${API_BASE_URL}/programs`);
  if (!response.ok) {
    throw new Error('Failed to fetch programs');
  }
  return response.json();
};

// Leaders API
export const leadersAPI = {
  // Get all leaders (public)
  getAll: async () => {
    const response = await fetch(`${API_BASE_URL}/leaders`);
    if (!response.ok) {
      throw new Error('Failed to fetch leaders');
    }
    return response.json();
  },

  // Get a single leader (public)
  getById: async (id) => {
    const response = await fetch(`${API_BASE_URL}/leaders/${id}`);
    if (!response.ok) {
      throw new Error('Failed to fetch leader');
    }
    return response.json();
  },

  // Create a new leader (admin only)
  create: async (formData) => {
    const token = localStorage.getItem('adminToken');
    const response = await fetch(`${API_BASE_URL}/leaders`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${token}`,
      },
      body: formData, // FormData for file upload
    });
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'Failed to create leader');
    }
    return response.json();
  },

  // Update a leader (admin only)
  update: async (id, formData) => {
    const token = localStorage.getItem('adminToken');
    const response = await fetch(`${API_BASE_URL}/leaders/${id}`, {
      method: 'PUT',
      headers: {
        'Authorization': `Bearer ${token}`,
      },
      body: formData, // FormData for file upload
    });
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'Failed to update leader');
    }
    return response.json();
  },

  // Delete a leader (admin only)
  delete: async (id) => {
    const token = localStorage.getItem('adminToken');
    const response = await fetch(`${API_BASE_URL}/leaders/${id}`, {
      method: 'DELETE',
      headers: {
        'Authorization': `Bearer ${token}`,
      },
    });
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'Failed to delete leader');
    }
    return response.json();
  },

  // Update sort order for multiple leaders (admin only)
  updateSortOrder: async (leadersData) => {
    const token = localStorage.getItem('adminToken');
    const response = await fetch(`${API_BASE_URL}/leaders/sort/update`, {
      method: 'PUT',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ leaders: leadersData }),
    });
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'Failed to update sort order');
    }
    return response.json();
  }
};

// Existing API functions
export const submitJoinForm = async (formData) => {
  const response = await fetch(`${API_BASE_URL}/join`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(formData),
  });
  return response.json();
};

export const submitContactForm = async (formData) => {
  const response = await fetch(`${API_BASE_URL}/contact`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(formData),
  });
  return response.json();
};

export const submitDonationForm = async (formData) => {
  const response = await fetch(`${API_BASE_URL}/donate`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(formData),
  });
  return response.json();
};

// Campaigns API
export const campaignsAPI = {
  // Get all campaigns (public)
  getAll: async (page = 1, limit = 100) => {
    const response = await fetch(`${API_BASE_URL}/campaigns?page=${page}&limit=${limit}`);
    if (!response.ok) {
      throw new Error('Failed to fetch campaigns');
    }
    return response.json();
  },

  // Get a single campaign (public)
  getById: async (id) => {
    const response = await fetch(`${API_BASE_URL}/campaigns/${id}`);
    if (!response.ok) {
      throw new Error('Failed to fetch campaign');
    }
    return response.json();
  },

  // Get campaign by slug (public)
  getBySlug: async (slug) => {
    const response = await fetch(`${API_BASE_URL}/campaigns/${slug}`);
    if (!response.ok) {
      throw new Error('Failed to fetch campaign');
    }
    return response.json();
  },

  // Create a new campaign (admin only)
  create: async (formData) => {
    const token = localStorage.getItem('adminToken');
    const response = await fetch(`${API_BASE_URL}/campaigns`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${token}`,
      },
      body: formData, // FormData for file upload
    });
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'Failed to create campaign');
    }
    return response.json();
  },

  // Update a campaign (admin only)
  update: async (id, formData) => {
    const token = localStorage.getItem('adminToken');
    const response = await fetch(`${API_BASE_URL}/campaigns/${id}`, {
      method: 'PUT',
      headers: {
        'Authorization': `Bearer ${token}`,
      },
      body: formData, // FormData for file upload
    });
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'Failed to update campaign');
    }
    return response.json();
  },

  // Delete a campaign (admin only)
  delete: async (id) => {
    const token = localStorage.getItem('adminToken');
    const response = await fetch(`${API_BASE_URL}/campaigns/${id}`, {
      method: 'DELETE',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
    });
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'Failed to delete campaign');
    }
    return response.json();
  }
};

// Re-export module APIs defined in folder-based files
export { productsAPI } from './api/productsAPI';
export { ordersAPI } from './api/ordersAPI';